import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Plus, X, User, Briefcase, Clock, ChevronDown, CheckCircle2,
  Trash2, AlertTriangle, Pencil, CalendarDays, Mail, Loader2
} from 'lucide-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import axios from 'axios';
import { ROLES, useAuth } from '../../contexts/AuthContext';
import { getAuthHeaders } from '../../services/authService';

// ─── PALETA ───────────────────────────────────────────────────────────────────
const AGENT_PALETTE = [
  { bg: '#04d9ff', text: '#0a0020' },
  { bg: '#f97316', text: '#0a0020' },
  { bg: '#a855f7', text: '#ffffff' },
  { bg: '#f43f5e', text: '#ffffff' },
  { bg: '#facc15', text: '#0a0020' },
  { bg: '#00ffb3', text: '#0a0020' },
  { bg: '#60a5fa', text: '#0a0020' },
  { bg: '#fb7185', text: '#0a0020' },
  { bg: '#34d399', text: '#0a0020' },
  { bg: '#e879f9', text: '#0a0020' },
  { bg: '#fbbf24', text: '#0a0020' },
  { bg: '#38bdf8', text: '#0a0020' },
];
const agentColorMap = new Map();
let colorIndex = 0;

function getAgentColor(nombre) {
  const key = String(nombre || '').trim().toLowerCase();
  if (!key) return { bg: '#10b981', text: '#0a0020' };
  if (!agentColorMap.has(key)) {
    agentColorMap.set(key, AGENT_PALETTE[colorIndex % AGENT_PALETTE.length]);
    colorIndex++;
  }
  return agentColorMap.get(key);
}

function getNameFromTitle(title = '') {
  return title.split(/\s[—–-]\s/)[0].trim();
}

function formatCalendarDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// ─── COMPONENTE PRINCIPAL ──────────────────────────────────────────────────────
export default function CalendarioTurnos() {
  const { hasPermission, user } = useAuth();
  const canEditTurnos = hasPermission('ops.turnos.write');
  const canManageTurnoCatalog = user?.roles?.some(role =>
    [ROLES.KLOUD_OPS_GERENTE, ROLES.KLOUD_OPS_COORDINADOR].includes(role)
  );

  const cardClasses = 'bg-white/5 backdrop-blur-xl border border-white/10';
  const inputClasses =
    'w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white focus:outline-none focus:border-[#04d9ff] transition-all appearance-none text-sm';

  // ── Datos ──────────────────────────────────────────────────────────────────
  const [events, setEvents]       = useState([]);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts]       = useState([]);
  const [catalogShifts, setCatalogShifts] = useState([]);
  const [teams, setTeams]         = useState([]);
  const [activeTab, setActiveTab] = useState('calendar');
  const [emailPeriod, setEmailPeriod] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [teamForm, setTeamForm]   = useState({ team_id: 'OPS_', team_name: '' });
  const [shiftForm, setShiftForm] = useState({
    shift_id: '',
    team_id: '',
    name: '',
    start_time: '08:00:00',
    end_time: '17:00:00',
    is_guard: false,
    is_calendar_enabled: true,
  });
  const [editingShiftId, setEditingShiftId] = useState('');
  const [catalogMessage, setCatalogMessage] = useState('');
  const [catalogError, setCatalogError]     = useState('');
  const [isSavingCatalog, setIsSavingCatalog] = useState(false);

  // ── Modal Nuevo Turno ──────────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen]   = useState(false);
  const [showSuccess, setShowSuccess]   = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [selectedDate, setSelectedDate]       = useState(new Date().toISOString().split('T')[0]);
  const [shiftTimes, setShiftTimes]           = useState({ start: '00:00:00', end: '00:00:00' });
  const [repeatWeekly, setRepeatWeekly]       = useState(false);

  // ── Popover contextual ─────────────────────────────────────────────────────
  const [popover, setPopover]     = useState(null); // { x, y, event }
  const popoverRef                = useRef(null);

  // ── Modal Eliminar ─────────────────────────────────────────────────────────
  const [selectedEvent, setSelectedEvent]         = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting]               = useState(false);
  const [deleteError, setDeleteError]             = useState('');

  // ── Modal Editar ───────────────────────────────────────────────────────────
  const [isEditModalOpen, setIsEditModalOpen]   = useState(false);
  const [editAgentId, setEditAgentId]           = useState('');
  const [editShiftId, setEditShiftId]           = useState('');
  const [editDate, setEditDate]                 = useState('');
  const [editShiftTimes, setEditShiftTimes]     = useState({ start: '00:00:00', end: '00:00:00' });
  const [isSavingEdit, setIsSavingEdit]         = useState(false);
  const [editError, setEditError]               = useState('');
  const [showEditSuccess, setShowEditSuccess]   = useState(false);

  const handleDatesSet = useCallback(({ view }) => {
    const lastDate = new Date(view.currentEnd);
    lastDate.setDate(lastDate.getDate() - 1);
    setEmailPeriod({
      start_date: formatCalendarDate(view.currentStart),
      end_date: formatCalendarDate(lastDate),
    });
    setEmailMessage('');
    setEmailError('');
  }, []);

  const sendScheduleEmail = async () => {
    if (!canManageTurnoCatalog || !emailPeriod || isSendingEmail) return;
    if (!window.confirm(`Enviar los horarios del ${emailPeriod.start_date} al ${emailPeriod.end_date} a todo el equipo?`)) return;
    setIsSendingEmail(true);
    setEmailMessage('');
    setEmailError('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/workforce/send-schedule-email`,
        emailPeriod,
        { headers: getAuthHeaders() }
      );
      setEmailMessage(response.data.message);
    } catch (error) {
      setEmailError(error.response?.data?.detail || 'No se pudo enviar el correo. Intenta de nuevo.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // ── Carga inicial ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [empRes, shiftRes, catalogShiftRes, calendarRes, teamRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/workforce/employees`),
          axios.get(`${process.env.REACT_APP_API_URL}/workforce/shifts`),
          axios.get(`${process.env.REACT_APP_API_URL}/workforce/shifts/catalog`),
          axios.get(`${process.env.REACT_APP_API_URL}/workforce/get-calendar-events`),
          axios.get(`${process.env.REACT_APP_API_URL}/workforce/teams`),
        ]);
        const emps = empRes.data;
        setEmployees(emps);
        setShifts(shiftRes.data);
        setCatalogShifts(catalogShiftRes.data);
        setTeams(teamRes.data);
        if (teamRes.data.length > 0) {
          setShiftForm(prev => prev.team_id ? prev : { ...prev, team_id: teamRes.data[0].team_id });
        }
        emps.forEach(e => getAgentColor(e.nombre));

        const coloredEvents = calendarRes.data.map(ev => {
          const color = getAgentColor(getNameFromTitle(ev.title));
          return {
            ...ev,
            backgroundColor: color.bg,
            borderColor: 'transparent',
            textColor: color.text,
            extendedProps: {
              agent_id:  ev.agent_id,
              shift_id:  ev.shift_id,
              work_date: ev.work_date,
            },
          };
        });

        setEvents(coloredEvents);
        if (emps.length > 0) setSelectedAgentId(emps[0].agent_id);
      } catch (err) {
        console.error('Error cargando datos:', err);
      }
    };
    fetchInitialData();
  }, []);

  // ── Cerrar popover al hacer click fuera ───────────────────────────────────
  useEffect(() => {
    if (!popover) return;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setPopover(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popover]);

  // ── Leyenda ────────────────────────────────────────────────────────────────
  const legendAgents = useMemo(() => {
    const namesInEvents = new Set(
      events.map(ev => getNameFromTitle(ev.title).toLowerCase()).filter(Boolean)
    );
    const source = namesInEvents.size > 0
      ? employees.filter(e => namesInEvents.has((e.nombre || '').toLowerCase()))
      : employees;
    return source.map(e => ({ ...e, color: getAgentColor(e.nombre) }));
  }, [employees, events]);

  // ── Click en evento → mostrar popover posicionado ────────────────────────
  const handleEventClick = useCallback((info) => {
    info.jsEvent.stopPropagation();
    const rect = info.el.getBoundingClientRect();
    const ep   = info.event.extendedProps;

    // Posicionamos el popover a la derecha del bloque del evento
    // Si se sale de la pantalla por la derecha, lo ponemos a la izquierda
    const spaceRight = window.innerWidth - rect.right;
    const x = spaceRight > 200 ? rect.right + 8 : rect.left - 208;
    const y = rect.top + window.scrollY;

    setPopover({
      x,
      y,
      event: {
        title:     info.event.title,
        agent_id:  ep.agent_id,
        shift_id:  ep.shift_id,
        work_date: ep.work_date,
        color:     info.event.backgroundColor,
        textColor: info.event.textColor,
      },
    });
  }, []);

  // ── Abrir modal eliminar desde popover ────────────────────────────────────
  const openDeleteModal = () => {
    setSelectedEvent(popover.event);
    setDeleteError('');
    setPopover(null);
    setIsDeleteModalOpen(true);
  };

  // ── Abrir modal editar desde popover ──────────────────────────────────────
  const openEditModal = () => {
    const ev = popover.event;
    setSelectedEvent(ev);
    setEditAgentId(String(ev.agent_id));
    setEditShiftId(ev.shift_id);
    setEditDate(ev.work_date);
    // Precarga horario del turno actual
    const shiftInfo = shifts.find(s => s.shift_id === ev.shift_id);
    if (shiftInfo) setEditShiftTimes({ start: shiftInfo.start, end: shiftInfo.end });
    setEditError('');
    setShowEditSuccess(false);
    setPopover(null);
    setIsEditModalOpen(true);
  };

  // ── Guardar edición ────────────────────────────────────────────────────────
  const saveEdit = async () => {
    if (!canEditTurnos) return;
    if (!editAgentId || !editShiftId || !editDate) {
      setEditError('Completa todos los campos.');
      return;
    }
    setIsSavingEdit(true);
    setEditError('');
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/workforce/update-turno`, {
        original_agent_id:  selectedEvent.agent_id,
        original_shift_id:  selectedEvent.shift_id,
        original_work_date: selectedEvent.work_date,
        new_agent_id:       parseInt(editAgentId),
        new_shift_id:       editShiftId,
        new_work_date:      editDate,
      });

      // Actualizar evento en el estado local
      const newEmployee  = employees.find(e => e.agent_id === parseInt(editAgentId));
      const newShift     = shifts.find(s => s.shift_id === editShiftId);
      const newColor     = getAgentColor(newEmployee?.nombre);

      setEvents(prev => prev.map(ev => {
        const ep = ev.extendedProps || {};
        if (
          ep.agent_id  === selectedEvent.agent_id &&
          ep.shift_id  === selectedEvent.shift_id &&
          ep.work_date === selectedEvent.work_date
        ) {
          return {
            ...ev,
            title:           `${newEmployee?.nombre} - ${newShift?.name}`,
            start:           `${editDate}T${editShiftTimes.start}`,
            end:             `${editDate}T${editShiftTimes.end}`,
            backgroundColor: newColor.bg,
            textColor:       newColor.text,
            extendedProps: {
              agent_id:  parseInt(editAgentId),
              shift_id:  editShiftId,
              work_date: editDate,
            },
          };
        }
        return ev;
      }));

      setShowEditSuccess(true);
      setTimeout(() => {
        setShowEditSuccess(false);
        setIsEditModalOpen(false);
      }, 2000);
    } catch (err) {
      console.error('Error al editar:', err);
      setEditError('No se pudo actualizar. Intenta de nuevo.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // ── Confirmar borrado ──────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!canEditTurnos) return;
    if (!selectedEvent) return;
    setIsDeleting(true);
    setDeleteError('');
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/workforce/delete-turno`, {
        data: {
          agent_id:  selectedEvent.agent_id,
          shift_id:  selectedEvent.shift_id,
          work_date: selectedEvent.work_date,
        },
      });
      setEvents(prev => prev.filter(ev => {
        const ep = ev.extendedProps || {};
        return !(
          ep.agent_id  === selectedEvent.agent_id &&
          ep.shift_id  === selectedEvent.shift_id &&
          ep.work_date === selectedEvent.work_date
        );
      }));
      setIsDeleteModalOpen(false);
      setSelectedEvent(null);
    } catch (err) {
      console.error('Error al borrar:', err);
      setDeleteError('No se pudo eliminar el registro. Intenta de nuevo.');
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Nuevo turno ────────────────────────────────────────────────────────────
  const handleShiftChange = (shiftId) => {
    setSelectedShiftId(shiftId);
    const s = shifts.find(s => s.shift_id === shiftId);
    if (s) setShiftTimes({ start: s.start, end: s.end });
  };

  const handleEditShiftChange = (shiftId) => {
    setEditShiftId(shiftId);
    const s = shifts.find(s => s.shift_id === shiftId);
    if (s) setEditShiftTimes({ start: s.start, end: s.end });
  };

  const refreshCatalogs = async () => {
    const [teamRes, shiftRes, catalogShiftRes] = await Promise.all([
      axios.get(`${process.env.REACT_APP_API_URL}/workforce/teams`),
      axios.get(`${process.env.REACT_APP_API_URL}/workforce/shifts`),
      axios.get(`${process.env.REACT_APP_API_URL}/workforce/shifts/catalog`),
    ]);
    setTeams(teamRes.data);
    setShifts(shiftRes.data);
    setCatalogShifts(catalogShiftRes.data);
    if (teamRes.data.length > 0) {
      setShiftForm(prev => prev.team_id ? prev : { ...prev, team_id: teamRes.data[0].team_id });
    }
  };

  const resetShiftForm = () => {
    setEditingShiftId('');
    setShiftForm(prev => ({
      ...prev,
      shift_id: '',
      name: '',
      start_time: '08:00:00',
      end_time: '17:00:00',
      is_guard: false,
      is_calendar_enabled: true,
    }));
  };

  const editShiftCatalog = (shift) => {
    setEditingShiftId(shift.shift_id);
    setShiftForm({
      shift_id: shift.shift_id,
      team_id: shift.team_id,
      name: shift.name,
      start_time: shift.start,
      end_time: shift.end,
      is_guard: Boolean(shift.is_guard),
      is_calendar_enabled: Boolean(shift.is_calendar_enabled),
    });
    setCatalogMessage('');
    setCatalogError('');
  };

  const saveTeam = async () => {
    if (!canManageTurnoCatalog) return;
    setIsSavingCatalog(true);
    setCatalogMessage('');
    setCatalogError('');
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/workforce/teams`, teamForm, { headers: getAuthHeaders() });
      await refreshCatalogs();
      setTeamForm({ team_id: 'OPS_', team_name: '' });
      setCatalogMessage('Equipo OPS guardado correctamente.');
    } catch (err) {
      console.error('Error guardando equipo:', err);
      setCatalogError(err.response?.data?.detail || 'No se pudo guardar el equipo OPS.');
    } finally {
      setIsSavingCatalog(false);
    }
  };

  const saveShiftCatalog = async () => {
    if (!canManageTurnoCatalog) return;
    setIsSavingCatalog(true);
    setCatalogMessage('');
    setCatalogError('');
    try {
      if (editingShiftId) {
        await axios.put(`${process.env.REACT_APP_API_URL}/workforce/shifts/${editingShiftId}`, shiftForm, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/workforce/shifts`, shiftForm, { headers: getAuthHeaders() });
      }
      await refreshCatalogs();
      resetShiftForm();
      setCatalogMessage(editingShiftId ? 'Turno OPS actualizado correctamente.' : 'Turno OPS guardado correctamente.');
    } catch (err) {
      console.error('Error guardando turno:', err);
      setCatalogError(err.response?.data?.detail || 'No se pudo guardar el turno OPS.');
    } finally {
      setIsSavingCatalog(false);
    }
  };

  const saveTurno = async () => {
    if (!canEditTurnos) return;
    if (!selectedShiftId || !selectedAgentId) {
      alert('Por favor selecciona empleado y turno');
      return;
    }
    const payload = [];
    const shiftInfo    = shifts.find(s => s.shift_id === selectedShiftId);
    const employeeInfo = employees.find(e => e.agent_id === parseInt(selectedAgentId));
    const agentColor   = getAgentColor(employeeInfo?.nombre);

    if (repeatWeekly) {
      const date   = new Date(selectedDate + 'T00:00:00');
      const day    = date.getDay();
      const diff   = date.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(date.setDate(diff));
      for (let i = 0; i < 5; i++) {
        const cur = new Date(monday);
        cur.setDate(monday.getDate() + i);
        payload.push({ agent_id: parseInt(selectedAgentId), shift_id: selectedShiftId, fecha: cur.toISOString().split('T')[0] });
      }
    } else {
      payload.push({ agent_id: parseInt(selectedAgentId), shift_id: selectedShiftId, fecha: selectedDate });
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/workforce/save-turnos`, payload);
      const newEvents = payload.map(t => ({
        id: String(Date.now() + Math.random()),
        title: `${employeeInfo?.nombre} - ${shiftInfo?.name}`,
        start: `${t.fecha}T${shiftTimes.start}`,
        end:   `${t.fecha}T${shiftTimes.end}`,
        backgroundColor: agentColor.bg,
        borderColor: 'transparent',
        textColor: agentColor.text,
        extendedProps: { agent_id: parseInt(selectedAgentId), shift_id: selectedShiftId, work_date: t.fecha },
      }));
      setEvents(prev => [...prev, ...newEvents]);
      setShowSuccess(true);
      setTimeout(() => { setShowSuccess(false); setIsModalOpen(false); }, 2500);
    } catch (err) {
      console.error('Error al guardar:', err);
      alert('Hubo un problema al sincronizar con BigQuery');
    }
  };

  // ── Helpers UI ─────────────────────────────────────────────────────────────
  const AgentSelect = ({ value, onChange, pl }) => (
    <div className="relative">
      <select value={value} onChange={e => onChange(e.target.value)} className={inputClasses}
        style={{ paddingLeft: value ? '2.5rem' : '1rem' }}>
        {employees.map((emp, i) => (
          <option key={i} value={emp.agent_id}>{emp.nombre}</option>
        ))}
      </select>
      {value && (
        <span className="absolute left-4 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full ring-1 ring-white/20"
          style={{ backgroundColor: getAgentColor(employees.find(e => String(e.agent_id) === String(value))?.nombre).bg }} />
      )}
      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 opacity-30 pointer-events-none" />
    </div>
  );

  const ShiftSelect = ({ value, onChange }) => {
    const filteredShifts = shifts.filter(s => !s.team_id || s.team_id.startsWith('OPS_'));
    return (
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} className={inputClasses}>
          <option value="">Seleccione un turno...</option>
          {filteredShifts.map((s, i) => (
            <option key={i} value={s.shift_id}>{s.name} ({s.start} - {s.end})</option>
          ))}
        </select>
        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 opacity-30 pointer-events-none" />
      </div>
    );
  };

  const renderCatalogPanel = (type) => {
    const isTeams = type === 'teams';
    return (
      <div className={`${cardClasses} rounded-[2.5rem] p-8 shadow-2xl border-white/5 w-full`}>
        <div className="flex items-start justify-between gap-6 mb-8">
          <div>
            <p className="text-[10px] font-bold text-[#04d9ff] uppercase tracking-[0.35em] mb-2">
              Administracion OPS
            </p>
            <h2 className="text-3xl font-black text-white uppercase tracking-tight">
              {isTeams ? 'Equipos OPS' : 'Turnos OPS'}
            </h2>
          </div>
          <div className="px-4 py-2 rounded-2xl bg-[#00ffb3]/10 border border-[#00ffb3]/20 text-[#00ffb3] text-[10px] font-black uppercase tracking-widest">
            Solo OPS
          </div>
        </div>

        {catalogMessage && <p className="mb-5 text-sm font-bold text-[#00ffb3]">{catalogMessage}</p>}
        {catalogError && <p className="mb-5 text-sm font-bold text-red-400">{catalogError}</p>}

        {isTeams ? (
          <div className="grid grid-cols-1 xl:grid-cols-[0.85fr_1.15fr] gap-6">
            <div className="bg-black/20 border border-white/10 rounded-[2rem] p-6 space-y-5">
              <div>
                <label className="text-[10px] font-bold text-[#04d9ff] uppercase tracking-[0.3em] block mb-2">ID Equipo</label>
                <input value={teamForm.team_id} onChange={e => setTeamForm(prev => ({ ...prev, team_id: e.target.value }))} className={inputClasses} placeholder="OPS_N4" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-[#04d9ff] uppercase tracking-[0.3em] block mb-2">Nombre</label>
                <input value={teamForm.team_name} onChange={e => setTeamForm(prev => ({ ...prev, team_name: e.target.value }))} className={inputClasses} placeholder="Ops N4" />
              </div>
              <button onClick={saveTeam} disabled={isSavingCatalog} className="w-full px-6 py-4 bg-[#00ffb3] text-[#0a0020] font-black rounded-2xl uppercase text-xs tracking-widest disabled:opacity-50">
                Guardar equipo
              </button>
            </div>
            <div className="bg-black/20 border border-white/10 rounded-[2rem] overflow-hidden">
              <div className="grid grid-cols-[1fr_1.4fr_0.8fr] px-5 py-3 text-[9px] text-white/30 uppercase tracking-widest font-bold border-b border-white/10">
                <span>ID</span><span>Equipo</span><span>Servicio</span>
              </div>
              {teams.map(team => (
                <div key={team.team_id} className="grid grid-cols-[1fr_1.4fr_0.8fr] px-5 py-4 text-sm border-b border-white/5 last:border-b-0">
                  <span className="font-mono text-[#04d9ff]">{team.team_id}</span>
                  <span className="font-bold text-white">{team.team_name}</span>
                  <span className="text-white/50">{team.service_type}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.1fr] gap-6">
            <div className="bg-black/20 border border-white/10 rounded-[2rem] p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold text-[#04d9ff] uppercase tracking-[0.3em] block mb-2">ID Turno</label>
                  <input value={shiftForm.shift_id} onChange={e => setShiftForm(prev => ({ ...prev, shift_id: e.target.value }))} disabled={Boolean(editingShiftId)} className={`${inputClasses} disabled:opacity-50 disabled:cursor-not-allowed`} placeholder="OPS_N4_T1" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-[#04d9ff] uppercase tracking-[0.3em] block mb-2">Equipo</label>
                  <select value={shiftForm.team_id} onChange={e => setShiftForm(prev => ({ ...prev, team_id: e.target.value }))} className={inputClasses}>
                    {teams.map(team => <option key={team.team_id} value={team.team_id}>{team.team_name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold text-[#04d9ff] uppercase tracking-[0.3em] block mb-2">Nombre</label>
                <input value={shiftForm.name} onChange={e => setShiftForm(prev => ({ ...prev, name: e.target.value }))} className={inputClasses} placeholder="T1 Ops N4" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold text-[#04d9ff] uppercase tracking-[0.3em] block mb-2">Inicio</label>
                  <input value={shiftForm.start_time} onChange={e => setShiftForm(prev => ({ ...prev, start_time: e.target.value }))} className={inputClasses} placeholder="08:00:00" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-[#04d9ff] uppercase tracking-[0.3em] block mb-2">Fin</label>
                  <input value={shiftForm.end_time} onChange={e => setShiftForm(prev => ({ ...prev, end_time: e.target.value }))} className={inputClasses} placeholder="17:00:00" />
                </div>
              </div>
              <label className="flex items-center gap-4 cursor-pointer p-4 hover:bg-white/5 rounded-[1.5rem] transition-all border border-white/5">
                <input type="checkbox" checked={shiftForm.is_guard} onChange={e => setShiftForm(prev => ({ ...prev, is_guard: e.target.checked }))} className="sr-only peer" />
                <div className="w-8 h-8 bg-white/5 rounded-xl border border-white/10 peer-checked:bg-[#00ffb3] flex items-center justify-center transition-all">
                  {shiftForm.is_guard && <X className="text-[#0a0020] stroke-[4]" size={16} />}
                </div>
                <span className="block text-sm font-bold text-white/80">Turno de guardia</span>
              </label>
              <label className="flex items-center gap-4 cursor-pointer p-4 hover:bg-white/5 rounded-[1.5rem] transition-all border border-white/5">
                <input type="checkbox" checked={shiftForm.is_calendar_enabled} onChange={e => setShiftForm(prev => ({ ...prev, is_calendar_enabled: e.target.checked }))} className="sr-only peer" />
                <div className="w-8 h-8 bg-white/5 rounded-xl border border-white/10 peer-checked:bg-[#04d9ff] flex items-center justify-center transition-all">
                  {shiftForm.is_calendar_enabled && <CheckCircle2 className="text-[#0a0020] stroke-[4]" size={16} />}
                </div>
                <span className="block text-sm font-bold text-white/80">Mostrar en calendario</span>
              </label>
              <div className="flex gap-3">
                {editingShiftId && (
                  <button onClick={resetShiftForm} disabled={isSavingCatalog} className="px-5 py-4 border border-white/10 text-white/50 hover:text-white rounded-2xl uppercase text-xs font-black tracking-widest disabled:opacity-50">
                    Cancelar
                  </button>
                )}
                <button onClick={saveShiftCatalog} disabled={isSavingCatalog || teams.length === 0} className="flex-1 px-6 py-4 bg-[#00ffb3] text-[#0a0020] font-black rounded-2xl uppercase text-xs tracking-widest disabled:opacity-50">
                  {editingShiftId ? 'Actualizar turno' : 'Guardar turno'}
                </button>
              </div>
            </div>
            <div className="bg-black/20 border border-white/10 rounded-[2rem] overflow-hidden">
              <div className="grid grid-cols-[0.9fr_1.1fr_0.8fr_0.9fr_0.7fr_0.5fr] px-5 py-3 text-[9px] text-white/30 uppercase tracking-widest font-bold border-b border-white/10">
                <span>ID</span><span>Nombre</span><span>Equipo</span><span>Horario</span><span>Visible</span><span></span>
              </div>
              {catalogShifts.map(shift => (
                <div key={shift.shift_id} className="grid grid-cols-[0.9fr_1.1fr_0.8fr_0.9fr_0.7fr_0.5fr] px-5 py-4 text-sm border-b border-white/5 last:border-b-0 items-center">
                  <span className="font-mono text-[#04d9ff] break-all">{shift.shift_id}</span>
                  <span className="font-bold text-white">{shift.name}</span>
                  <span className="text-white/50">{shift.team_id}</span>
                  <span className="text-white/70 font-mono">{shift.start} - {shift.end}</span>
                  <span className={shift.is_calendar_enabled ? 'text-[#00ffb3] font-black' : 'text-white/30 font-bold'}>
                    {shift.is_calendar_enabled ? 'Si' : 'No'}
                  </span>
                  <button onClick={() => editShiftCatalog(shift)} className="justify-self-end p-2 rounded-xl bg-white/5 hover:bg-[#04d9ff]/10 text-[#04d9ff] transition-colors" title="Editar turno">
                    <Pencil size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0a0020] text-white font-sans selection:bg-[#04d9ff]/30">

      {/* Header */}
      <div className="px-4 sm:px-8 py-6 flex flex-wrap gap-5 justify-between items-center bg-gradient-to-b from-white/5 to-transparent">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-4xl font-black tracking-tight bg-gradient-to-r from-[#04d9ff] via-[#00ffb3] to-[#04d9ff] bg-clip-text text-transparent uppercase">
            Calendario de Turnos Kloud
          </h1>
          <p className="text-xs font-medium tracking-widest uppercase opacity-50 mt-1">Operaciones Kloud Público</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 max-w-full">
        {canManageTurnoCatalog && activeTab === 'calendar' && (
          <button
            onClick={sendScheduleEmail}
            disabled={isSendingEmail || !emailPeriod}
            className="flex items-center justify-center gap-2 px-5 py-4 bg-white/5 border border-[#04d9ff]/40 text-[#04d9ff] font-bold rounded-2xl transition-colors hover:bg-[#04d9ff]/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSendingEmail ? <Loader2 size={20} className="animate-spin" /> : <Mail size={20} />}
            <span>{isSendingEmail ? 'Enviando...' : 'Enviar horarios'}</span>
          </button>
        )}
        {canEditTurnos && activeTab === 'calendar' && (
          <button
            onClick={() => { setIsModalOpen(true); setShowSuccess(false); }}
            className="flex items-center justify-center gap-3 px-5 py-4 bg-[#00ffb3] text-[#0a0020] font-bold rounded-2xl transition-all hover:scale-105 shadow-[0_0_20px_rgba(0,255,179,0.3)]"
          >
            <Plus size={20} strokeWidth={3} />
            <span>NUEVO REGISTRO TURNO</span>
          </button>
        )}
        </div>
      </div>

      {canManageTurnoCatalog && activeTab === 'calendar' && (emailMessage || emailError) && (
        <p role={emailError ? 'alert' : 'status'} className={`px-8 pb-3 text-sm font-semibold ${emailError ? 'text-red-400' : 'text-[#00ffb3]'}`}>
          {emailError || emailMessage}
        </p>
      )}

      {canManageTurnoCatalog && (
        <div className="px-8 pb-2 flex gap-3">
          {[
            { id: 'calendar', label: 'Calendario' },
            { id: 'teams', label: 'Equipos OPS' },
            { id: 'shifts', label: 'Turnos OPS' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setCatalogMessage('');
                setCatalogError('');
              }}
              className={`px-5 py-3 rounded-2xl text-xs font-black uppercase tracking-widest border transition-all ${
                activeTab === tab.id
                  ? 'bg-[#04d9ff] text-[#0a0020] border-[#04d9ff] shadow-[0_0_18px_rgba(4,217,255,0.25)]'
                  : 'bg-white/5 text-white/50 border-white/10 hover:text-white hover:border-white/20'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Layout */}
      {(!canManageTurnoCatalog || activeTab === 'calendar') ? (
      <div className="p-4 sm:p-8 flex flex-col xl:flex-row gap-6 items-start">

        {/* Calendario */}
        <div className={`${cardClasses} flex-1 min-w-0 w-full rounded-[2.5rem] p-3 sm:p-8 shadow-2xl overflow-hidden border-white/5`}>
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
            locale={esLocale}
            events={events}
            datesSet={handleDatesSet}
            height="75vh"
            allDaySlot={false}
            slotEventOverlap={false}
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            scrollTime="08:00:00"
            slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            eventClick={handleEventClick}
            eventContent={(info) => {
              const bg  = info.event.backgroundColor || '#10b981';
              const txt = info.event.textColor || '#0a0020';
              return (
                <div className="h-full w-full flex items-center justify-center rounded-lg group relative overflow-hidden"
                  style={{ background: bg }}>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all" />
                  <div className="text-[10px] font-bold tracking-wider relative z-10"
                    style={{ color: txt, writingMode: 'vertical-rl', transform: 'rotate(180deg)', textAlign: 'center' }}>
                    {getNameFromTitle(info.event.title)}
                  </div>
                </div>
              );
            }}
          />
        </div>

        {/* Sidebar leyenda */}
        <aside className={`${cardClasses} rounded-[2rem] p-6 shadow-xl flex-shrink-0 w-full xl:w-56 xl:sticky top-8`} style={{ minWidth: '13rem' }}>
          <p className="text-[10px] font-bold text-[#04d9ff] uppercase tracking-[0.3em] mb-5 flex items-center gap-2">
            <User size={13} /> Agentes
          </p>
          {legendAgents.length === 0 ? (
            <p className="text-white/20 text-xs italic">Sin datos aún</p>
          ) : (
            <ul className="space-y-3">
              {legendAgents.map(agent => (
                <li key={agent.agent_id} className="flex items-center gap-3 group">
                  <span className="flex-shrink-0 w-3 h-3 rounded-full ring-2 ring-white/10 group-hover:scale-125 transition-transform"
                    style={{ backgroundColor: agent.color.bg }} />
                  <span className="text-xs font-semibold text-white/80 group-hover:text-white transition-colors leading-tight truncate">
                    {agent.nombre}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-6 pt-5 border-t border-white/5">
            <p className="text-[9px] text-white/20 uppercase tracking-widest leading-relaxed">
              Color único por agente · sincronizado con BigQuery
            </p>
          </div>
          <div className="mt-4 p-3 bg-white/3 rounded-xl border border-white/5">
            <p className="text-[9px] text-white/30 leading-relaxed">
              💡 Haz click en un turno para editarlo o eliminarlo
            </p>
          </div>
        </aside>
      </div>
      ) : (
        <div className="p-8 flex gap-6 items-start">
          {renderCatalogPanel(activeTab === 'teams' ? 'teams' : 'shifts')}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          POPOVER CONTEXTUAL
      ═══════════════════════════════════════════════════════════════════════ */}
      {popover && (
        <div
          ref={popoverRef}
          className="fixed z-[60] animate-fade-in-up"
          style={{ top: popover.y, left: popover.x }}
        >
          <div className="w-52 bg-[#150035] border border-white/15 rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] overflow-hidden">
            {/* Cabecera del popover con color del agente */}
            <div className="px-4 py-3 border-b border-white/5" style={{ borderTop: `3px solid ${popover.event.color}` }}>
              <p className="text-[9px] text-white/30 uppercase tracking-widest">Turno seleccionado</p>
              <p className="text-sm font-black text-white truncate mt-0.5">{getNameFromTitle(popover.event.title)}</p>
              <p className="text-[10px] font-semibold mt-0.5" style={{ color: popover.event.color }}>
                {popover.event.title.split(/\s[—–-]\s/)[1] || ''}
              </p>
              <p className="text-[9px] text-white/30 mt-1 flex items-center gap-1">
                <CalendarDays size={10} /> {popover.event.work_date}
              </p>
            </div>

            {/* Opciones */}
            <div className="p-2 space-y-1">
              {canEditTurnos && (
                <>
                  <button
                    onClick={openEditModal}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#04d9ff]/10 text-left transition-colors group"
                  >
                    <div className="w-7 h-7 rounded-lg bg-[#04d9ff]/10 flex items-center justify-center group-hover:bg-[#04d9ff]/20 transition-colors">
                      <Pencil size={13} className="text-[#04d9ff]" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white">Editar turno</p>
                      <p className="text-[9px] text-white/30">Cambiar agente, turno o fecha</p>
                    </div>
                  </button>

                  <button
                    onClick={openDeleteModal}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-red-500/10 text-left transition-colors group"
                  >
                    <div className="w-7 h-7 rounded-lg bg-red-500/10 flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
                      <Trash2 size={13} className="text-red-400" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-red-400">Eliminar turno</p>
                      <p className="text-[9px] text-white/30">Borrar de BigQuery</p>
                    </div>
                  </button>
                </>
              )}

            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          MODAL EDITAR
      ═══════════════════════════════════════════════════════════════════════ */}
      {isEditModalOpen && selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-[#0a0020]/90 backdrop-blur-md"
            onClick={() => { if (!isSavingEdit) setIsEditModalOpen(false); }} />

          <div className="relative w-full max-w-lg bg-[#150035] border border-white/10 rounded-[2.5rem] shadow-2xl z-10 overflow-hidden animate-fade-in-up">
            <div className="h-1.5 w-full" style={{ backgroundColor: selectedEvent.color }} />

            {!showEditSuccess ? (
              <>
                {/* Header */}
                <div className="px-8 pt-7 pb-5 border-b border-white/5 flex justify-between items-center">
                  <div>
                    <p className="text-[9px] text-white/30 uppercase tracking-[0.3em]">Editando turno</p>
                    <h3 className="text-2xl font-black text-white flex items-center gap-3 mt-1">
                      <Pencil size={20} className="text-[#04d9ff]" />
                      {getNameFromTitle(selectedEvent.title)}
                    </h3>
                  </div>
                  <button onClick={() => setIsEditModalOpen(false)} disabled={isSavingEdit}
                    className="text-white/20 hover:text-white bg-white/5 p-2 rounded-xl transition-colors disabled:opacity-30">
                    <X size={18} />
                  </button>
                </div>

                <div className="px-8 py-7 space-y-6">
                  {/* Agente */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-[#04d9ff] uppercase tracking-[0.3em] flex items-center gap-2">
                      <User size={13} /> Agente
                    </label>
                    <AgentSelect value={editAgentId} onChange={setEditAgentId} />
                  </div>

                  {/* Turno */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-[#04d9ff] uppercase tracking-[0.3em] flex items-center gap-2">
                      <Briefcase size={13} /> Turno
                    </label>
                    <ShiftSelect value={editShiftId} onChange={handleEditShiftChange} />
                  </div>

                  {/* Fecha + preview horario */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                      <label className="text-[9px] text-white/30 uppercase font-bold tracking-widest block mb-2">Nueva Fecha</label>
                      <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)}
                        className="bg-transparent text-sm w-full outline-none font-bold text-[#04d9ff]" />
                    </div>
                    <div className="p-4 bg-white/5 rounded-2xl border border-white/5 flex items-center gap-3">
                      <div className="p-1.5 bg-[#00ffb3]/10 rounded-lg text-[#00ffb3]"><Clock size={16} /></div>
                      <div>
                        <p className="text-[9px] text-white/30 uppercase font-bold tracking-widest">Horario</p>
                        <p className="text-xs font-black text-white">{editShiftTimes.start} - {editShiftTimes.end}</p>
                      </div>
                    </div>
                  </div>

                  {/* Datos originales (referencia) */}
                  <div className="p-4 bg-white/3 rounded-2xl border border-white/5">
                    <p className="text-[9px] text-white/20 uppercase tracking-widest mb-2">Registro original</p>
                    <p className="text-[10px] text-white/40 font-mono">
                      {selectedEvent.work_date} · Agent {selectedEvent.agent_id} · {selectedEvent.shift_id}
                    </p>
                  </div>

                  {editError && (
                    <p className="text-xs text-red-400 text-center font-semibold">{editError}</p>
                  )}
                </div>

                <div className="px-8 pb-8 flex gap-3">
                  <button onClick={() => setIsEditModalOpen(false)} disabled={isSavingEdit}
                    className="flex-1 px-4 py-3.5 text-white/40 hover:text-white font-bold uppercase text-xs border border-white/10 rounded-2xl hover:border-white/20 transition-all disabled:opacity-30">
                    Cancelar
                  </button>
                  <button onClick={saveEdit} disabled={isSavingEdit}
                    className="flex-1 px-4 py-3.5 bg-gradient-to-r from-[#04d9ff] to-[#00ffb3] text-[#0a0020] font-black rounded-2xl flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 disabled:opacity-50">
                    {isSavingEdit ? (
                      <>
                        <div className="w-4 h-4 border-2 border-[#0a0020]/30 border-t-[#0a0020] rounded-full animate-spin" />
                        <span className="text-xs uppercase">Guardando...</span>
                      </>
                    ) : (
                      <>
                        <Pencil size={14} />
                        <span className="text-xs uppercase">Guardar Cambios</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              /* Success edición */
              <div className="p-16 flex flex-col items-center text-center space-y-6 animate-fade-in-up">
                <div className="w-20 h-20 bg-[#04d9ff]/10 rounded-full flex items-center justify-center border-4 border-[#04d9ff] shadow-[0_0_30px_rgba(4,217,255,0.3)]">
                  <CheckCircle2 className="text-[#04d9ff] animate-pulse" size={44} />
                </div>
                <div>
                  <h2 className="text-3xl font-black text-white uppercase tracking-tighter">¡Turno Actualizado!</h2>
                  <p className="text-[#04d9ff] font-bold tracking-widest uppercase text-[10px] mt-2">Sincronizado con BigQuery</p>
                </div>
                <div className="w-full max-w-xs bg-white/5 h-1.5 rounded-full overflow-hidden">
                  <div className="bg-[#04d9ff] h-full animate-progress" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          MODAL ELIMINAR
      ═══════════════════════════════════════════════════════════════════════ */}
      {isDeleteModalOpen && selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-[#0a0020]/90 backdrop-blur-md"
            onClick={() => { if (!isDeleting) setIsDeleteModalOpen(false); }} />
          <div className="relative w-full max-w-md bg-[#150035] border border-white/10 rounded-[2.5rem] shadow-2xl z-10 overflow-hidden animate-fade-in-up">
            <div className="h-1.5 w-full" style={{ backgroundColor: selectedEvent.color }} />

            <div className="px-8 pt-7 pb-5 flex justify-between items-start">
              <div>
                <p className="text-[9px] font-bold text-white/30 uppercase tracking-[0.3em] mb-1">Detalle del turno</p>
                <h3 className="text-xl font-black text-white">{getNameFromTitle(selectedEvent.title)}</h3>
                <p className="text-sm font-semibold mt-0.5" style={{ color: selectedEvent.color }}>
                  {selectedEvent.title.split(/\s[—–-]\s/)[1] || ''}
                </p>
              </div>
              <button onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}
                className="text-white/20 hover:text-white bg-white/5 p-2 rounded-xl transition-colors disabled:opacity-30">
                <X size={18} />
              </button>
            </div>

            <div className="px-8 pb-6 space-y-3">
              <div className="grid grid-cols-3 gap-3 text-center">
                {[
                  { label: 'Fecha',     value: selectedEvent.work_date },
                  { label: 'Agente ID', value: selectedEvent.agent_id },
                  { label: 'Turno ID',  value: selectedEvent.shift_id },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-white/5 rounded-2xl p-4 border border-white/5">
                    <p className="text-[8px] text-white/30 uppercase tracking-widest mb-1">{label}</p>
                    <p className="text-sm font-black text-white break-all">{value}</p>
                  </div>
                ))}
              </div>
              <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-2xl flex items-start gap-3">
                <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-300/80 leading-relaxed">
                  Esta acción eliminará permanentemente el registro de BigQuery. No se puede deshacer.
                </p>
              </div>
              {deleteError && <p className="text-xs text-red-400 text-center font-semibold">{deleteError}</p>}
            </div>

            <div className="px-8 pb-8 flex gap-3">
              <button onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}
                className="flex-1 px-4 py-3.5 text-white/40 hover:text-white font-bold uppercase text-xs border border-white/10 rounded-2xl hover:border-white/20 transition-all disabled:opacity-30">
                Cancelar
              </button>
              <button onClick={confirmDelete} disabled={isDeleting}
                className="flex-1 px-4 py-3.5 bg-red-500/90 hover:bg-red-500 text-white font-black rounded-2xl flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 disabled:opacity-50">
                {isDeleting ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span className="text-xs uppercase">Eliminando...</span></>
                ) : (
                  <><Trash2 size={15} /><span className="text-xs uppercase">Eliminar Registro</span></>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          MODAL NUEVO TURNO
      ═══════════════════════════════════════════════════════════════════════ */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="fixed inset-0 bg-[#0a0020]/90 backdrop-blur-md"
            onClick={() => { setIsModalOpen(false); setShowSuccess(false); }} />
          <div className="relative w-full max-w-xl bg-[#150035] border border-white/10 rounded-[3rem] shadow-2xl z-10 overflow-hidden animate-fade-in-up">
            {!showSuccess ? (
              <>
                <div className="px-10 py-8 border-b border-white/5 flex justify-between items-center text-[#00ffb3]">
                  <h3 className="text-3xl font-black uppercase flex items-center gap-4">
                    <Plus size={24} strokeWidth={3} /> Nuevo Registro
                  </h3>
                  <button onClick={() => setIsModalOpen(false)}
                    className="text-white/20 hover:text-white bg-white/5 p-2 rounded-xl transition-colors"><X /></button>
                </div>
                <div className="px-10 py-10 space-y-8">
                  <div className="space-y-3">
                    <label className="text-[10px] font-bold text-[#04d9ff] uppercase tracking-[0.3em] flex items-center gap-2"><User size={14} /> Empleado</label>
                    <AgentSelect value={selectedAgentId} onChange={setSelectedAgentId} />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-bold text-[#04d9ff] uppercase tracking-[0.3em] flex items-center gap-2"><Briefcase size={14} /> Turno</label>
                    <ShiftSelect value={selectedShiftId} onChange={handleShiftChange} />
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="p-5 bg-white/5 rounded-[2rem] border border-white/5">
                      <label className="text-[9px] text-white/30 uppercase font-bold tracking-widest block mb-2">Fecha de Inicio</label>
                      <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                        className="bg-transparent text-sm w-full outline-none font-bold text-[#04d9ff]" />
                    </div>
                    <div className="p-5 bg-white/5 rounded-[2rem] border border-white/5 flex items-center gap-4">
                      <div className="p-2 bg-[#00ffb3]/10 rounded-xl text-[#00ffb3]"><Clock size={20} /></div>
                      <div>
                        <p className="text-[9px] text-white/30 uppercase font-bold tracking-widest">Horario</p>
                        <p className="text-sm font-black text-white">{shiftTimes.start} - {shiftTimes.end}</p>
                      </div>
                    </div>
                  </div>
                  <label className="flex items-center gap-4 cursor-pointer p-4 hover:bg-white/5 rounded-[1.5rem] transition-all border border-white/5">
                    <input type="checkbox" checked={repeatWeekly} onChange={e => setRepeatWeekly(e.target.checked)} className="sr-only peer" />
                    <div className="w-8 h-8 bg-white/5 rounded-xl border border-white/10 peer-checked:bg-[#00ffb3] flex items-center justify-center transition-all">
                      {repeatWeekly && <X className="text-[#0a0020] stroke-[4]" size={16} />}
                    </div>
                    <span className="block text-sm font-bold text-white/80">Repetir Lunes a Viernes</span>
                  </label>
                </div>
                <div className="px-10 py-8 bg-black/20 border-t border-white/5 flex justify-end gap-4">
                  <button onClick={() => setIsModalOpen(false)}
                    className="px-6 py-3 text-white/30 hover:text-white font-bold uppercase text-xs">Cancelar</button>
                  <button onClick={saveTurno}
                    className="px-10 py-4 bg-gradient-to-r from-[#04d9ff] to-[#00ffb3] text-[#0a0020] font-black rounded-2xl shadow-lg transform hover:-translate-y-1 transition-all">
                    Confirmar Registro
                  </button>
                </div>
              </>
            ) : (
              <div className="p-20 flex flex-col items-center justify-center text-center space-y-8 animate-fade-in-up">
                <div className="w-24 h-24 bg-[#00ffb3]/10 rounded-full flex items-center justify-center border-4 border-[#00ffb3] shadow-[0_0_30px_rgba(0,255,179,0.4)]">
                  <CheckCircle2 className="text-[#00ffb3] animate-pulse" size={56} />
                </div>
                <div>
                  <h2 className="text-4xl font-black text-white uppercase tracking-tighter">¡Registro Exitoso!</h2>
                  <p className="text-[#04d9ff] font-bold tracking-widest uppercase text-[10px] mt-2">Sincronizado con BigQuery Kloud</p>
                </div>
                <div className="w-full max-w-xs bg-white/5 h-1.5 rounded-full overflow-hidden">
                  <div className="bg-[#00ffb3] h-full animate-progress" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ESTILOS */}
      <style>{`
        .fc { --fc-border-color: rgba(255,255,255,0.05); --fc-text-color: #ffffff; }
        .fc .fc-header-toolbar { flex-wrap: wrap; gap: 0.75rem; }
        .fc .fc-toolbar-title { font-size: 1.25rem; }
        .fc-theme-standard td, .fc-theme-standard th { border: 1px solid rgba(255,255,255,0.05) !important; }
        .fc-col-header-cell-cushion { color: #04d9ff !important; font-weight: 800 !important; font-size: 0.75rem; text-transform: uppercase; }
        .fc .fc-button-primary { background: rgba(255,255,255,0.05) !important; border: 1px solid rgba(255,255,255,0.1) !important; font-size: 0.6rem; font-weight: 800; }
        .fc .fc-button-active { background: #04d9ff !important; color: #0a0020 !important; border-color: #04d9ff !important; }
        .fc-v-event { border-radius: 8px !important; border: none !important; box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important; }
        .fc-daygrid-event { border-radius: 4px !important; padding: 2px 4px !important; margin-top: 2px !important; }
        .fc-daygrid-event .fc-event-title { font-weight: 800 !important; font-size: 0.65rem !important; text-transform: uppercase; }
        .fc-daygrid-event-dot { display: none !important; }
        .fc-event { cursor: pointer !important; }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in-up { animation: fadeInUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
        @keyframes progress { from { width: 0%; } to { width: 100%; } }
        .animate-progress { animation: progress 2.5s ease-in-out; }
        select option { background-color: #150035; color: white; }
      `}</style>
    </div>
  );
}